import React from 'react';
import { Link } from 'react-router';
import {
  CfmPerTonReason,
  PeriodAirflowRecord,
  SystemAirflowCalibration,
  SystemAirflowCleanWindow,
  SystemAirflowClip,
  SystemAirflowResponse,
  SystemTonnage,
} from '../../types';
import { qualityLabel } from '../../utils/equipmentQuality';
import { systemTonnageAnswer, tonnageValueText } from '../../utils/tonnageWords';
import { clipLabel } from '../../utils/clipLabel';
import { grilleNumbers, isNotCountedSlot, notCountedSlotText } from './FilterConfiguration';
import {
  AirflowCalibration,
  AirflowCurve,
  AirflowRangeNote,
  BoundWord,
  NoteBadge,
  ReportingLagNote,
  ShortEvidenceBadge,
  combinedBound,
  perTonBoundWords,
  rangeWords,
  SystemAirflowCalibrationBadge,
  cleanDaysText,
  totalRangeLabel,
} from '../AirflowCalibration';

/**
 * Plan 33 Phase 6 (first pass) — summed system airflow for multi-Clip HVAC
 * systems, rendered under the HVAC system info on the Overview tab. Shows the
 * total of the Clips that have a clean-filter estimate, every constituent
 * Clip, and a plain-language reason for each Clip that cannot contribute
 * (missing filter, no catalog curve, nothing computed).
 *
 * FS-666: the figure is each Clip's CLEAN-FILTER estimate — the average of
 * its first up-to-three computed days in the current filter period — not its
 * latest day. The daily estimate inverts the filter's clean catalog curve at
 * the day's pressure, so it drifts up as the filter loads and the latest day
 * overstated the system. It is an estimate of initial airflow from the
 * earliest computed days after the current baseline, and is labeled as such.
 *
 * Against a pre-FS-566 API it renders exactly what it always did, and
 * nothing for a single-Clip system. Against an FS-566 API (see
 * isSystemAirflowV2) it renders for every system with a member, single-Clip
 * included, and adds the server's CFM per ton — see SystemAirflowV2 below.
 *
 * FS-721: each filter's face velocity (its airflow over its net face area,
 * both from the server) sits in a per-filter table under the system figures.
 *
 * FS-747: the clean days are the first running days in this filter period
 * (up to three, within 14 days of the first day the blower ran in it), and each value carries its
 * calibration, shown as a badge beside the CFM (AirflowCalibration), with
 * the days it came from. A response without those fields renders as before.
 *
 * Plan 127 Phase 3: a value read from the stored record may sit outside its
 * filter curve; AirflowRangeNote says so beside the CFM wherever one shows.
 *
 * FS-878: a curve-limited value reads "under N CFM" / "over N CFM" (and so
 * does a figure made from it: a total, a CFM per ton, a face velocity), with
 * the full line under a main value; a value from fewer than three clean days
 * is marked "Based on N days". The words are AirflowCalibration's.
 */

const REASON_LABEL: Record<Exclude<SystemAirflowClip['status'], 'ok'>, string> = {
  no_filter_info: 'no filter recorded in the app',
  no_curve_match: 'no pressure-drop curve for the recorded filter',
  not_computed: 'airflow not yet computed',
  no_clean_airflow: 'no clean airflow for this filter',
};

/** FS-747: how the clean-filter value is taken, under a response that carries the new rule. */
export const CLEAN_RULE =
  "Clean-filter airflow: each Clip's average CFM over its first running days in this filter " +
  "period (up to three, within 14 days of the first day the blower ran in the period), summed across " +
  'the Clips on this HVAC system. A value that is not calibrated is shown and labeled.';

/** FS-747: the heading line for a Clip whose clean window closed without a CFM. */
export const NO_CLEAN_AIRFLOW = 'No clean airflow for this filter';

/** FS-747: does this response carry the clean-window contract? Any one Clip is enough. */
const hasCleanWindow = (data: SystemAirflowResponse): boolean =>
  Array.isArray(data.clips) && data.clips.some((clip) => !!clip.clean_window);

/**
 * FS-666 deploy-gap guard. The API ships before the client, so this bundle
 * can meet a pre-FS-666 response (or one from a rollback) whose entries
 * carry the LATEST daily value as latest_cfm and no clean fields at all.
 * That number is not a clean-filter estimate and must not be shown as one,
 * so the block is omitted unless every entry carries clean_cfm_days —
 * which the new API sets on contributors and non-contributors alike (0 is a
 * valid value). An empty clips list has nothing to misread.
 */
const hasCleanFilterContract = (data: SystemAirflowResponse): boolean =>
  Array.isArray(data.clips) &&
  data.clips.every((clip) => typeof (clip as Partial<SystemAirflowClip>).clean_cfm_days === 'number');

/**
 * FS-825 — "Owner · Upstairs Loft (#243)": the shared Clip label, so two
 * Clips in one home differ. With no description and no filter name, the id
 * alone, as before.
 */
const clipText = (clip: SystemAirflowClip) => {
  const label = clipLabel({ ...clip, name: null });
  return label ? `${label} (#${clip.device_id})` : `#${clip.device_id}`;
};

/** "avg of 3 days 2026-05-07–2026-05-09", or "1 day 2026-05-07". */
const sampleSpan = (clip: SystemAirflowClip) =>
  clip.clean_cfm_days === 1
    ? `1 day ${clip.clean_cfm_first_date}`
    : `avg of ${clip.clean_cfm_days} days ${clip.clean_cfm_first_date}–${clip.clean_cfm_last_date}`;

/** FS-747: the clean days and window state when the response has them, else the FS-666 span. */
const clipSpan = (clip: SystemAirflowClip) =>
  cleanDaysText(clip.clean_window, clip.clean_cfm_days) ?? sampleSpan(clip);

const ClipRow: React.FC<{ clip: SystemAirflowClip; isSelf: boolean }> = ({ clip, isSelf }) => (
  <li className="flex items-baseline justify-between gap-4 py-1">
    <span>
      {isSelf ? (
        <span className="font-medium">{clipText(clip)}</span>
      ) : (
        <Link to={`/devices/${clip.device_id}/overview`} className="text-link hover:underline">
          {clipText(clip)}
        </Link>
      )}
      {isSelf && <span className="ml-1 text-xs text-muted-foreground">this device</span>}
    </span>
    {clip.status === 'ok' ? (
      <span className="text-right">
        {/* Each Clip is rounded on its own for display; the API's total is
            the sum of the UNROUNDED means rounded once, so the rows need not
            add up to it to the CFM. */}
        <span className="whitespace-nowrap font-mono">
          {Math.round(clip.clean_cfm!).toLocaleString()} CFM
        </span>
        <span className="block text-xs text-muted-foreground">clean-filter estimate, {sampleSpan(clip)}</span>
      </span>
    ) : (
      <span className="text-right text-amber-700">{REASON_LABEL[clip.status]}</span>
    )}
  </li>
);

const LegacySystemAirflow: React.FC<{ data: SystemAirflowResponse; deviceId: number }> = ({
  data,
  deviceId,
}) => {
  // Only meaningful when this Clip is one of several on the HVAC system.
  if (data.clip_count + data.hidden_count < 2) return null;
  if (!hasCleanFilterContract(data)) return null;

  const memberCount = data.clip_count + data.hidden_count;

  return (
    <div className="mt-4 border-t pt-4">
      <h4 className="font-semibold text-sm mb-2">System airflow</h4>
      {data.total_cfm != null ? (
        <p className="text-sm mb-2">
          <span className="text-2xl font-semibold">{data.total_cfm.toLocaleString()}</span>
          <span className="ml-1 text-gray-600">CFM combined</span>
          <span className="ml-2 text-xs text-muted-foreground">clean-filter estimate</span>
          {data.complete ? (
            <span className="ml-2 text-xs text-muted-foreground">
              all {memberCount} Clips have an estimate
            </span>
          ) : (
            <span className="ml-2 text-xs font-medium text-amber-700">
              partial — {data.contributing_count} of {memberCount} Clips
            </span>
          )}
        </p>
      ) : (
        <p className="text-sm mb-2 text-amber-700">
          Not enough information to calculate system airflow — see each Clip below.
        </p>
      )}
      <ul className="text-sm divide-y divide-gray-100">
        {data.clips.map((clip) => (
          <ClipRow key={clip.device_id} clip={clip} isSelf={clip.device_id === deviceId} />
        ))}
      </ul>
      {data.hidden_count > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {data.hidden_count} more Clip{data.hidden_count === 1 ? '' : 's'} on this system{' '}
          {data.hidden_count === 1 ? 'is' : 'are'} outside your scope and not included.
        </p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Clean-filter airflow estimate: each Clip&apos;s average of up to its first three computed
        days from its current baseline (or its first full days after a filter change), summed
        across the Clips on this HVAC system.
      </p>
    </div>
  );
};

/**
 * FS-566 — does this response carry the CFM-per-ton contract? A pre-FS-566
 * API sends none of these keys; an FS-566 API sends all of them (possibly
 * null). Any one is enough, so a half-deployed server still reads as new.
 */
const V2_FIELDS = [
  'partial_cfm',
  'tonnage',
  'cfm_per_ton',
  'cfm_per_ton_reason',
  'projection_stale',
] as const;

export const isSystemAirflowV2 = (data: SystemAirflowResponse): boolean =>
  V2_FIELDS.some((key) => key in data);

/** One Clip on the system, as the system figure used it. */
interface SystemMember {
  device_id: number;
  /**
   * FS-825 — the shared Clip label (utils/clipLabel): the description and the
   * filter name, with the serial leading only when there is no description.
   */
  label: string | null;
  /** The Clip's serial, kept for the hover text. */
  serial: string | null;
  cfm: number | null;
  contributed: boolean;
}

/**
 * The Clips the figure was built from, read off `clips`. The FS-566 API adds
 * device_name, cfm and contributed to each entry; where one is missing (a
 * server before FS-566) the entry's older fields stand in, and a Clip counts
 * when its status is 'ok' — the same rule the server applies.
 *
 * FS-825: a member is named by the same label as the header. `device_name`
 * is the serial, so it was every row's name while the description was the
 * owner's and the same on each; now it leads only when there is no
 * description, and is otherwise in the hover text.
 */
const membersOf = (data: SystemAirflowResponse): SystemMember[] => {
  if (!Array.isArray(data.clips)) return [];
  return data.clips.map((clip) => {
    const serial = clip.device_name ?? clip.name ?? null;
    return {
      device_id: clip.device_id,
      label: clipLabel({ ...clip, name: serial }) || null,
      serial,
      cfm: clip.cfm !== undefined ? clip.cfm : clip.clean_cfm,
      contributed: typeof clip.contributed === 'boolean' ? clip.contributed : clip.status === 'ok',
    };
  });
};

const memberLabel = (m: SystemMember) =>
  m.label ? `${m.label} (#${m.device_id})` : `#${m.device_id}`;

const cfmText = (n: number) => Math.round(n).toLocaleString();

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * FS-838 — "4 t, Entered by the homeowner on the outdoor unit". The answer
 * line names the unit the projection took the tonnage from and the evidence,
 * in `tonnageWords`' sentence, so this tile and the Overview's Tonnage line
 * cannot word one value two ways. An "up to" figure reads `up to 4 t`. The
 * title adds the quality rung.
 */
export const tonnageWords = (
  tonnage: SystemTonnage | null | undefined,
  fallbackValue: number | null | undefined,
): {
  text: string;
  title: string;
  value: number;
  valueText: string;
  detail: string | null;
  upperBound: boolean;
} | null => {
  const value = tonnage?.value ?? fallbackValue ?? null;
  if (value == null) return null;
  const upperBound = tonnage?.upper_bound === true;
  const valueText = tonnageValueText(value, upperBound);
  const detail = tonnage ? systemTonnageAnswer({ ...tonnage, value }) : null;
  const title =
    `System tonnage used for CFM per ton: ${value} t. ` +
    (detail ? `${detail}. ` : '') +
    `Quality: ${tonnage?.quality ? qualityLabel(tonnage.quality) : 'not reported'}.`;
  return {
    text: detail ? `${valueText}, ${detail}` : valueText,
    title,
    value,
    valueText,
    detail,
    upperBound,
  };
};

const NEEDS_EVERY_CLIP = 'needs an estimate from every Clip on the system';

/**
 * Why the server did not return CFM/ton, in one line. It sits under the
 * tile's "CFM per ton" label, so it does not name the figure again.
 */
const CFM_PER_TON_REFUSAL: Record<CfmPerTonReason, string> = {
  no_tonnage: 'no tonnage recorded for this system',
  tonnage_from_btu: 'not shown: the tonnage was derived from a BTU rating',
  projection_stale: 'not shown: app data is still being refreshed',
  no_airflow: NEEDS_EVERY_CLIP,
  partial_airflow: NEEDS_EVERY_CLIP,
};

const refusalText = (reason: string) =>
  CFM_PER_TON_REFUSAL[reason as CfmPerTonReason] ?? `not shown (${reason})`;

const MemberName: React.FC<{ member: SystemMember; isSelf: boolean }> = ({
  member,
  isSelf,
}) =>
  isSelf ? (
    <>
      <span className="font-medium" title={member.serial ? `Clip ${member.serial}` : undefined}>
        {memberLabel(member)}
      </span>
      <span className="ml-1 text-xs text-muted-foreground">this device</span>
    </>
  ) : (
    <Link
      to={`/devices/${member.device_id}/overview`}
      className="text-link hover:underline"
      title={`Open the overview for device #${member.device_id}${member.serial ? ` (Clip ${member.serial})` : ''}`}
    >
      {memberLabel(member)}
    </Link>
  );

/**
 * FS-721 deploy-order guard. The FS-721 API sends face_velocity_missing on
 * every entry, contributing or not, so its absence from all of them means an
 * older server: the Size and Face velocity columns are left off rather than
 * printing a dash on every row.
 */
const hasFaceVelocityContract = (data: SystemAirflowResponse): boolean =>
  Array.isArray(data.clips) && data.clips.some((c) => Array.isArray(c.face_velocity_missing));

/**
 * "20 × 25": the nominal size, smaller side first as filter labels print it.
 * The app stores 20x25 filters as width 25, length 20.
 */
const sizeText = (size: { width_in: number; length_in: number }) =>
  `${Math.min(size.width_in, size.length_in)} × ${Math.max(size.width_in, size.length_in)}`;

const FACE_VELOCITY_MISSING: Record<'filter_size' | 'airflow', string> = {
  filter_size: 'no filter size recorded',
  airflow: 'no airflow estimate yet',
};

/**
 * FS-747: is this Clip's airflow not calibrated? Its `calibration` (the badge
 * beside the CFM) decides when the response carries it, so the face velocity
 * never says something different about the same value; `cfm_calibrated`
 * stands in only for an API before FS-747.
 */
const notCalibrated = (clip: SystemAirflowClip): boolean =>
  clip.calibration
    ? clip.calibration.state !== null && clip.calibration.state !== 'calibrated'
    : clip.cfm_calibrated === false;

/** FS-878: a curve-limited Clip value's full line, led by a space; '' when it is not limited. */
const limitedLine = (clip: SystemAirflowClip | undefined): string => {
  const range = rangeWords(clip?.range_status);
  return range?.bound ? ` ${range.title}` : '';
};

/**
 * The face-velocity cell's title: how the server's number was made, or what
 * kept it from being made. The numbers in it are the server's; nothing here
 * divides.
 */
const faceVelocityTitle = (clip: SystemAirflowClip | undefined, cfm: number | null): string => {
  if (clip?.face_velocity_fpm == null) {
    const missing = (clip?.face_velocity_missing ?? [])
      .map((key) => FACE_VELOCITY_MISSING[key])
      .filter(Boolean);
    return missing.length > 0
      ? `No face velocity: ${missing.join(' and ')}.`
      : 'No face velocity was returned for this Clip.';
  }
  const airflow = cfm != null ? `Airflow ${cfmText(cfm)} CFM` : 'Airflow';
  const area =
    clip.filter_net_area_sqft != null
      ? `net face area ${clip.filter_net_area_sqft.toFixed(2)} sq ft`
      : 'net face area';
  let title = `${airflow} ÷ ${area}.`;
  if (clip.filter_size) {
    title +=
      ` Net area is the nominal ${sizeText(clip.filter_size)} size less 0.75 in on each ` +
      'edge for the frame.';
  }
  if (notCalibrated(clip)) title += ' The airflow is not calibrated.';
  return title + limitedLine(clip);
};

const NUMBER_CELL = 'py-1.5 pl-4 text-right align-top font-mono tabular-nums whitespace-nowrap';
const WORDS_CELL = 'py-1.5 pl-4 text-right align-top';
const DASH = '—';

/**
 * Plan 127 Phase 3 — a combined or partial total that includes a Clip whose
 * pressure sat outside its filter curve says so beside the total, with the
 * affected Clips named in the title. Each Clip's own row says which way.
 */
const TotalRangeNote: React.FC<{
  members: SystemMember[];
  clipById: Map<number, SystemAirflowClip>;
}> = ({ members, clipById }) => {
  const affected = members.flatMap((m) => {
    const words = rangeWords(clipById.get(m.device_id)?.range_status);
    return words ? [{ m, words }] : [];
  });
  const label = totalRangeLabel(members.map((m) => clipById.get(m.device_id)?.range_status));
  if (affected.length === 0 || !label) return null;
  const title = affected.map((a) => `${memberLabel(a.m)}: ${a.words.title}`).join(' ');
  return (
    <>
      {' '}
      <span
        className="ml-1 align-middle text-xs text-amber-700"
        title={title}
        data-testid="system-airflow-range"
      >
        {label}
      </span>
    </>
  );
};

/**
 * One Clip on the system: its name and the days behind its estimate, its
 * filter's nominal size, its clean-filter airflow and that filter's face
 * velocity. The face velocity is printed as the server sent it, with no
 * rating of any kind; an uncalibrated airflow is said in plain gray words.
 */
const FilterRow: React.FC<{
  member: SystemMember;
  clip: SystemAirflowClip | undefined;
  isSelf: boolean;
  showFace: boolean;
}> = ({ member, clip, isSelf, showFace }) => (
  <tr data-testid="system-airflow-filter-row">
    <td className="py-1.5 pr-4 align-top">
      <MemberName member={member} isSelf={isSelf} />
      {member.contributed && clip && clip.status === 'ok' && typeof clip.clean_cfm_days === 'number' && (
        <span className="block text-xs text-muted-foreground">{clipSpan(clip)}</span>
      )}
      {/* FS-747: said once per Clip, here beside its clean days (not again in the tile). */}
      {member.contributed && clip && clip.status === 'ok' && <ReportingLagNote window={clip.clean_window} />}
      {!member.contributed && clip && cleanDaysText(clip.clean_window, clip.clean_cfm_days) && (
        <span className="block text-xs text-muted-foreground">
          {cleanDaysText(clip.clean_window, clip.clean_cfm_days)}
        </span>
      )}
    </td>
    {showFace && (
      <td
        className={NUMBER_CELL}
        title={
          clip?.filter_size
            ? 'Nominal filter size in inches, from the filter recorded in the app.'
            : 'No filter size is recorded in the app for this Clip.'
        }
      >
        {clip?.filter_size ? sizeText(clip.filter_size) : DASH}
      </td>
    )}
    {member.contributed ? (
      member.cfm != null ? (
        <td className={NUMBER_CELL}>
          <span
            title={`This Clip's clean-filter airflow estimate, rounded for display.${limitedLine(clip)}`}
            data-testid={`clip-airflow-cfm-${member.device_id}`}
          >
            <BoundWord bound={rangeWords(clip?.range_status)?.bound} />
            {cfmText(member.cfm)} CFM
          </span>
          <span className="font-sans">
            <AirflowCalibration calibration={clip?.calibration} cfmDays={clip?.clean_cfm_days} />
            <AirflowRangeNote status={clip?.range_status} />
          </span>
        </td>
      ) : (
        <td className={`${WORDS_CELL} text-amber-700`}>counted, but no figure was returned</td>
      )
    ) : (
      <td className={`${WORDS_CELL} text-amber-700`}>
        {clip?.status === 'no_clean_airflow' ? (
          <>
            {NO_CLEAN_AIRFLOW}
            <AirflowCurve calibration={clip.calibration} />
          </>
        ) : (
          <>
            no airflow estimate yet
            {clip && clip.status !== 'ok' && REASON_LABEL[clip.status]
              ? ` (${REASON_LABEL[clip.status]})`
              : ''}
          </>
        )}
      </td>
    )}
    {showFace && (
      <td
        className={NUMBER_CELL}
        data-testid="system-airflow-face-velocity"
        title={faceVelocityTitle(clip, member.cfm)}
      >
        {clip?.face_velocity_fpm != null ? (
          <>
            <BoundWord bound={rangeWords(clip.range_status)?.bound} />
            {clip.face_velocity_fpm.toLocaleString()} ft/min
            {/* FS-747: with a calibration the badge beside the airflow says
                it; this older marker stands in only without one. */}
            {!clip.calibration && clip.cfm_calibrated === false && (
              <span className="block font-sans text-xs text-muted-foreground">airflow not calibrated</span>
            )}
          </>
        ) : (
          DASH
        )}
      </td>
    )}
  </tr>
);

/** A Not counted line with no Clip figures: dashes in the number columns. */
const NotCountedLine: React.FC<{
  title: string;
  columns: number;
  testId?: string;
  children: React.ReactNode;
}> = ({ title, columns, testId, children }) => (
  <tr className="text-gray-600" title={title}>
    <td className="py-1.5 pr-4 align-top" data-testid={testId} title={title}>
      {children}
    </td>
    {Array.from({ length: columns - 1 }, (_, i) => (
      <td key={i} className={`${NUMBER_CELL} text-muted-foreground`}>
        {DASH}
      </td>
    ))}
  </tr>
);

const SLOT_TITLE: Record<string, string> = {
  none: 'No Clip is recorded in this filter slot, so the airflow through it is not in the figure.',
  hidden:
    'A Clip sits in this slot but is outside the devices you can see, so its airflow is not in the figure.',
  conflict:
    'More than one filter record names the same Clip, so this slot is not counted as covered.',
  unresolved:
    'The app names a Clip in this slot that is not on this HVAC system, so the slot is not covered.',
};

const Tile: React.FC<{ testId?: string; title?: string; children: React.ReactNode }> = ({
  testId,
  title,
  children,
}) => (
  <div className="rounded-md border border-border px-4 py-3" data-testid={testId} title={title}>
    {children}
  </div>
);

const TILE_NUMBER = 'block text-2xl font-semibold';
const TILE_LABEL = 'block text-sm text-gray-600';

/**
 * FS-566 (with FS-560), laid out for FS-721 — the system figures as three
 * tiles (airflow, the server's CFM per ton, the tonnage behind it), then a
 * per-filter table: every Clip with its filter's size, its airflow and its
 * face velocity, and under "Not counted" everything that keeps the figure
 * partial.
 *
 * - Complete: the total, "CFM combined" only when there are two or more
 *   Clips. When the server returned no CFM/ton, a dash and one line why.
 * - Not complete: never a combined total. The partial sum says how many
 *   Clips it covers, every Clip left out is named, scope-hidden Clips are
 *   counted, and CFM/ton is refused.
 * - Face velocity is per filter only; there is no system face velocity.
 * Nothing here divides: CFM/ton and ft/min are the server's numbers or they
 * are not shown.
 */
const SystemAirflowV2: React.FC<{
  data: SystemAirflowResponse;
  deviceId: number;
  heading: string;
  headingTitle?: string;
}> = ({ data, deviceId, heading, headingTitle }) => {
  const members = membersOf(data);
  const hidden = data.hidden_count ?? 0;
  const memberCount = members.length + hidden;
  if (memberCount === 0) return null;

  const multi = memberCount >= 2;
  const counted = members.filter((m) => m.contributed);
  const notCounted = members.filter((m) => !m.contributed);
  const clipById = new Map<number, SystemAirflowClip>(
    (Array.isArray(data.clips) ? data.clips : []).map((c) => [c.device_id, c]),
  );
  const tonnage = tonnageWords(data.tonnage, data.hvac_tonnage);
  const showFace = hasFaceVelocityContract(data);
  const columns = showFace ? 4 : 2;
  // FS-593: a grille with no Clip, a Clip outside the caller's scope or a
  // name that is not on this system keeps the figure partial, so each is
  // named under "Not counted". A scope-hidden Clip named by its slot is not
  // counted a second time in the generic "outside your scope" line.
  const slotList = Array.isArray(data.slots) ? data.slots : [];
  const slotNumbers = grilleNumbers(slotList);
  const blockingSlots = slotList.filter(isNotCountedSlot);
  const hiddenInSlots = blockingSlots.filter((s) => s.state === 'hidden').length;
  const hiddenUnnamed = Math.max(0, hidden - hiddenInSlots);
  const hasNotCounted = notCounted.length > 0 || hiddenUnnamed > 0 || blockingSlots.length > 0;
  // FS-747: on a one-Clip system the figure IS that Clip's value, so its own
  // calibration sits beside it; a system of several gets the summary.
  const onlyClip = !multi && members.length === 1 ? clipById.get(members[0].device_id) : undefined;
  const newRule = hasCleanWindow(data);
  // A partial total with exactly one Clip in it (say one Clip and a grille
  // with none) is that Clip's value, so it carries that Clip's own badge,
  // reason and source, not the system summary.
  const soleContributor = counted.length === 1 ? clipById.get(counted[0].device_id) : undefined;

  // The airflow tile: a figure with its label, or a sentence saying why not.
  // FS-878: `bound` is "under"/"over" when the figure is curve-limited.
  let figure: {
    value: number;
    label: string;
    title: string;
    badge: React.ReactNode;
    bound: ReturnType<typeof combinedBound>;
  } | null = null;
  let noFigure: React.ReactNode = null;
  let perTonReason: string | null = null;

  if (data.complete) {
    if (data.total_cfm != null) {
      figure = {
        value: data.total_cfm,
        label: multi ? 'CFM combined' : 'CFM',
        title: multi
          ? `Sum of the clean-filter airflow estimates of all ${memberCount} Clips on this system.`
          : 'Clean-filter airflow estimate of the only Clip on this system.',
        badge: multi ? (
          <>
            <SystemAirflowCalibrationBadge state={data.calibration_state} />
            <TotalRangeNote members={counted} clipById={clipById} />
          </>
        ) : (
          <>
            <AirflowCalibration calibration={onlyClip?.calibration} cfmDays={onlyClip?.clean_cfm_days} />
            <ShortEvidenceBadge days={onlyClip?.clean_cfm_days} />
            <AirflowRangeNote status={onlyClip?.range_status} full />
          </>
        ),
        bound: multi
          ? combinedBound(counted.map((m) => clipById.get(m.device_id)?.range_status))
          : combinedBound([onlyClip?.range_status]),
      };
      if (data.cfm_per_ton == null && data.cfm_per_ton_reason) {
        perTonReason = refusalText(data.cfm_per_ton_reason);
      }
    } else {
      noFigure = 'Every Clip is counted, but no system total came back.';
    }
  } else {
    if (data.partial_cfm != null) {
      figure = {
        value: data.partial_cfm,
        label: `CFM from ${counted.length} of ${memberCount} Clips`,
        title:
          'Sum of the Clips that have a clean-filter estimate only. It is not the system ' +
          'total: the Clips listed as not counted are missing from it.',
        badge: soleContributor ? (
          <>
            <AirflowCalibration
              calibration={soleContributor.calibration}
              cfmDays={soleContributor.clean_cfm_days}
            />
            <ShortEvidenceBadge days={soleContributor.clean_cfm_days} />
            <AirflowRangeNote status={soleContributor.range_status} full />
          </>
        ) : (
          <>
            <SystemAirflowCalibrationBadge state={data.calibration_state} />
            <TotalRangeNote members={counted} clipById={clipById} />
          </>
        ),
        bound: combinedBound(counted.map((m) => clipById.get(m.device_id)?.range_status)),
      };
    } else if (!multi && onlyClip?.status === 'no_clean_airflow') {
      noFigure = (
        <>
          {NO_CLEAN_AIRFLOW}, so there is no system figure.
          <AirflowCurve calibration={onlyClip.calibration} />
        </>
      );
    } else if (counted.length === 0) {
      noFigure = multi
        ? `None of the ${memberCount} Clips on this system has an airflow estimate yet, so there is no system figure.`
        : 'This Clip has no airflow estimate yet, so there is no system figure.';
    } else {
      noFigure = `${counted.length} of ${memberCount} Clips have an estimate, but no partial total came back.`;
    }
    perTonReason = NEEDS_EVERY_CLIP;
  }

  const perTon = data.complete && data.total_cfm != null ? data.cfm_per_ton ?? null : null;
  // FS-838: divided by a maximum, the figure is a minimum. FS-878: made from
  // a curve-limited airflow, it is limited the same way.
  const perTonBound = perTonBoundWords(figure?.bound, tonnage?.upperBound === true);

  return (
    <div className="mt-4 border-t pt-4">
      <h4 className="font-semibold text-sm mb-3" title={headingTitle}>
        {heading}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile>
          {figure ? (
            <p data-testid="system-airflow-headline" title={figure.title}>
              <span className={TILE_NUMBER}>
                <BoundWord bound={figure.bound} />
                {cfmText(figure.value)}
              </span>{' '}
              <span className={TILE_LABEL}>
                {figure.label}
                {figure.badge}
              </span>
            </p>
          ) : (
            <>
              <span className={`${TILE_NUMBER} text-muted-foreground`} aria-hidden="true">
                {DASH}
              </span>
              <p className="text-sm text-amber-700" data-testid="system-airflow-headline">
                {noFigure}
              </p>
            </>
          )}
          <p className="mt-0.5 text-xs text-muted-foreground">
            clean-filter estimate
            {onlyClip && onlyClip.status === 'ok' && onlyClip.clean_window
              ? `, ${clipSpan(onlyClip)}`
              : ''}
          </p>
        </Tile>
        <Tile
          testId="system-airflow-per-ton"
          title={
            perTon != null
              ? 'Airflow per ton of cooling, computed by the server from the airflow figure and ' +
                'the system tonnage.' +
                perTonBound.note
              : undefined
          }
        >
          <span className={perTon != null ? TILE_NUMBER : `${TILE_NUMBER} text-muted-foreground`}>
            {perTon != null ? (
              <>
                <BoundWord bound={perTonBound.word} />
                {perTon.toLocaleString()}
              </>
            ) : (
              DASH
            )}
          </span>{' '}
          <span className={TILE_LABEL}>CFM per ton</span>{' '}
          {perTonReason && (
            <span
              className="mt-0.5 block text-xs text-amber-700"
              data-testid="system-airflow-per-ton-refusal"
              title="CFM per ton is computed by the server only for a complete system with a usable tonnage."
            >
              {perTonReason}
            </span>
          )}
        </Tile>
        <Tile
          testId="system-airflow-tonnage"
          title={tonnage ? tonnage.title : 'No system tonnage is recorded for this HVAC system.'}
        >
          <span className={tonnage ? TILE_NUMBER : `${TILE_NUMBER} text-muted-foreground`}>
            {tonnage ? tonnage.valueText : DASH}
          </span>
          {/* FS-838 §2.4: "4 t, Entered by …" — the value and the answer
              joined by a comma, as `tonnageWords.text` says it. */}
          {tonnage?.detail ? ', ' : ' '}
          <span className={TILE_LABEL}>
            {tonnage ? (tonnage.detail ?? 'system tonnage') : 'no tonnage recorded'}
          </span>
        </Tile>
      </div>
      {(members.length > 0 || hasNotCounted) && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="pb-1 pr-4 text-left font-medium">
                  Filter
                </th>
                {showFace && (
                  <th scope="col" className="pb-1 pl-4 text-right font-medium">
                    Size
                  </th>
                )}
                <th scope="col" className="pb-1 pl-4 text-right font-medium">
                  Airflow
                </th>
                {showFace && (
                  <th scope="col" className="pb-1 pl-4 text-right font-medium">
                    Face velocity
                  </th>
                )}
              </tr>
            </thead>
            {counted.length > 0 && (
              <tbody className="divide-y divide-gray-100 border-t border-border">
                {counted.map((m) => (
                  <FilterRow
                    key={m.device_id}
                    member={m}
                    clip={clipById.get(m.device_id)}
                    isSelf={m.device_id === deviceId}
                    showFace={showFace}
                  />
                ))}
              </tbody>
            )}
            {hasNotCounted && (
              <tbody
                className="divide-y divide-gray-100 border-t border-border"
                data-testid="system-airflow-not-counted"
              >
                <tr>
                  <th
                    scope="rowgroup"
                    colSpan={columns}
                    className="pt-3 pb-1 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    Not counted
                  </th>
                </tr>
                {notCounted.map((m) => (
                  <FilterRow
                    key={m.device_id}
                    member={m}
                    clip={clipById.get(m.device_id)}
                    isSelf={m.device_id === deviceId}
                    showFace={showFace}
                  />
                ))}
                {blockingSlots.map((slot) => (
                  <NotCountedLine
                    key={`slot-${slot.id}`}
                    title={SLOT_TITLE[slot.state] ?? SLOT_TITLE.unresolved}
                    columns={columns}
                    testId="system-airflow-not-counted-slot"
                  >
                    {notCountedSlotText(slot, slotNumbers.get(slot.id))}
                  </NotCountedLine>
                ))}
                {hiddenUnnamed > 0 && (
                  <NotCountedLine
                    title="These Clips are on this HVAC system but outside the devices you can see, so their airflow is not in the figure."
                    columns={columns}
                  >
                    {hiddenUnnamed} {hiddenInSlots > 0 ? 'more ' : ''}
                    {plural(hiddenUnnamed, 'Clip', 'Clips')} outside your scope
                  </NotCountedLine>
                )}
              </tbody>
            )}
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        {newRule ? (
          CLEAN_RULE
        ) : (
          <>
            Clean-filter airflow estimate: each Clip&apos;s average of up to its first three computed
            days from its current baseline (or its first full days after a filter change), summed
            across the Clips on this HVAC system.
          </>
        )}
        {showFace && (
          <>
            {' '}
            Face velocity is each filter&apos;s airflow divided by its net face area (nominal size
            less 0.75 in per edge).
          </>
        )}
      </p>
    </div>
  );
};

/**
 * FS-748 — one Clip's own value, for the Analysis page's Airflow section on a
 * system of several Clips: the CFM with its calibration beside it and the
 * clean days beneath, or why there is none. The same number, rounded the same
 * way, as that Clip's row in the system block.
 */
export const ClipAirflow: React.FC<{ clip: SystemAirflowClip }> = ({ clip }) => {
  const cfm = clip.cfm !== undefined ? clip.cfm : clip.clean_cfm;
  const days = cleanDaysText(clip.clean_window, clip.clean_cfm_days);
  if (clip.status === 'ok' && cfm != null) {
    return (
      <div data-testid="clip-airflow">
        <p className="text-sm">
          <span
            data-testid="clip-airflow-value"
            title="This Clip's clean-filter airflow estimate for the current filter, rounded for display."
          >
            <span className="text-2xl font-semibold">
              <BoundWord bound={rangeWords(clip.range_status)?.bound} />
              {cfmText(cfm)}
            </span>{' '}
            <span className="text-gray-600">CFM</span>
          </span>
          <AirflowCalibration calibration={clip.calibration} cfmDays={clip.clean_cfm_days} />
          <ShortEvidenceBadge days={clip.clean_cfm_days} />
          <AirflowRangeNote status={clip.range_status} full />
        </p>
        <p className="text-xs text-muted-foreground">this Clip, clean-filter estimate, {clipSpan(clip)}</p>
        <ReportingLagNote window={clip.clean_window} />
      </div>
    );
  }
  return (
    <div data-testid="clip-airflow">
      <p className="text-sm text-amber-700">
        {clip.status === 'no_clean_airflow' ? (
          <>
            {NO_CLEAN_AIRFLOW}
            <AirflowCurve calibration={clip.calibration} />
          </>
        ) : (
          <>
            This Clip has no airflow estimate yet
            {clip.status !== 'ok' && REASON_LABEL[clip.status] ? ` (${REASON_LABEL[clip.status]})` : ''}
          </>
        )}
      </p>
      {days && <p className="text-xs text-muted-foreground">{days}</p>}
    </div>
  );
};

/**
 * Plan 127 Phase 3 — the calibration a stored record carries, in the shape
 * the badge reads. It comes from the record's own match snapshot, not the
 * device's match today; a past record names no scaled-from size or donor.
 * A record is computed on one match, so it is never 'partly': every clean
 * day is calibrated or none is.
 */
export const recordCalibration = (r: PeriodAirflowRecord): SystemAirflowCalibration => {
  const state =
    r.state !== undefined
      ? r.state
      : r.calibrated === true
        ? 'calibrated'
        : r.calibrated === false
          ? 'approximate'
          : null;
  const dates = Array.isArray(r.clean_dates) ? r.clean_dates : [];
  return {
    state,
    calibrated_days: state === 'calibrated' ? dates.length : 0,
    reason: r.reason ?? null,
    source: r.catalog_source ?? null,
    confidence: r.catalog_confidence ?? null,
    size_verified: r.catalog_size_verified ?? null,
    scaled_from: null,
    donor: null,
  };
};

/** A stored record's clean days as a closed window, for cleanDaysText and the late-start note. */
export const recordWindow = (r: PeriodAirflowRecord): SystemAirflowCleanWindow => {
  const dates = Array.isArray(r.clean_dates) ? r.clean_dates : [];
  const running = typeof r.running_days === 'number' ? r.running_days : dates.length;
  return {
    period_start: r.period_start,
    window_end: null,
    state: 'closed',
    running_days: Math.max(0, Math.min(3, running)) as SystemAirflowCleanWindow['running_days'],
    dates,
    window_start: r.window_start,
    reporting_lag_days: r.reporting_lag_days,
  };
};

/**
 * Plan 127 Phase 3 — a previous filter period's stored airflow record on the
 * Analysis page, laid out like the current value: the CFM with its
 * calibration and range note beside it, the clean days and any late start
 * beneath; or why there is no value.
 *
 * FS-878: a record may rest on only one or two clean days (a short period);
 * it is shown, marked "Based on N days".
 */
export const PeriodAirflowValue: React.FC<{ record: PeriodAirflowRecord }> = ({ record }) => {
  const calibration = recordCalibration(record);
  const window = recordWindow(record);
  // Every clean day of a record with a CFM carries one (one match for all).
  const cfmDays = record.cfm != null ? window.dates.length : 0;
  const days = cleanDaysText(window, cfmDays);
  if (record.cfm != null) {
    return (
      <div data-testid="period-airflow">
        <p className="text-sm">
          <span
            data-testid="period-airflow-value"
            title="This Clip's clean-filter airflow for this filter period, from its stored record, rounded for display."
          >
            <span className="text-2xl font-semibold">
              <BoundWord bound={rangeWords(record.range_status)?.bound} />
              {cfmText(record.cfm)}
            </span>{' '}
            <span className="text-gray-600">CFM</span>
          </span>
          <AirflowCalibration calibration={calibration} cfmDays={cfmDays} />
          <ShortEvidenceBadge days={cfmDays} />
          <AirflowRangeNote status={record.range_status} full />
        </p>
        <p className="text-xs text-muted-foreground">
          clean-filter estimate for this filter period{days ? `, ${days}` : ''}
        </p>
        <ReportingLagNote window={window} />
      </div>
    );
  }
  return (
    <div data-testid="period-airflow">
      <p className="text-sm text-amber-700">
        {NO_CLEAN_AIRFLOW}
        {record.catalog_id == null ? ` (${REASON_LABEL.no_curve_match})` : ''}
        <AirflowCurve calibration={calibration} />
      </p>
      {days && <p className="text-xs text-muted-foreground">{days}</p>}
      <ReportingLagNote window={window} />
    </div>
  );
};

/** FS-878 — the badge beside a past period's CFM per ton. */
export const TODAYS_TONNAGE = "using today's tonnage";

/** FS-878 — the line where a past period's CFM per ton is not built (several Clips or returns). */
export const PAST_PER_TON_NEEDS_EVERY_RETURN =
  "CFM per ton for a past period needs every return's airflow for the same dates.";

export type PastPeriodPerTon =
  | {
      kind: 'value';
      perTon: number;
      tonnage: NonNullable<ReturnType<typeof tonnageWords>>;
      bound: ReturnType<typeof perTonBoundWords>;
    }
  | { kind: 'several' }
  | { kind: 'refused'; reason: string };

/**
 * FS-878 — a previous filter period's CFM per ton: its stored CFM divided by
 * the system tonnage on file TODAY, read from the current system-airflow
 * response. Equipment history is not tracked, so today's tonnage is the only
 * one there is, and the figure is labeled so.
 *
 * This is the one CFM per ton the client divides itself; the current
 * period's stays the server's. It follows the server's rule (device-data-api
 * `cfmPerTonOf`): the tonnage's own refusal first, then an ineligible or
 * missing tonnage, and it rounds the CFM and then the quotient, as the server
 * rounds its total before dividing.
 *
 * Only a system with one Clip that covers every return has it: with several
 * Clips (or a return with no Clip) the period's per-ton would need every
 * return's airflow over the same dates, which is not built. Membership is
 * today's, from the same response. Null when the record has no CFM or the
 * device is on no HVAC system.
 */
export const pastPeriodPerTon = (
  record: PeriodAirflowRecord,
  data: SystemAirflowResponse,
): PastPeriodPerTon | null => {
  if (record.cfm == null) return null;
  const clips = Array.isArray(data.clips) ? data.clips : [];
  const memberCount = clips.length + (data.hidden_count ?? 0);
  // FS-593: with a slot list, a return with no Clip (or one that cannot be
  // counted) is air the Clip did not measure; with none, members alone decide.
  const slotsCovered = data.coverage ? data.coverage.complete !== false : true;
  // No HVAC system on record: the current view shows no system block either.
  if (memberCount === 0) return null;
  if (memberCount > 1 || !slotsCovered) return { kind: 'several' };
  const t = data.tonnage;
  if (t?.reason) return { kind: 'refused', reason: refusalText(t.reason) };
  if (!t || !t.eligible || t.value == null || !(t.value > 0)) {
    return { kind: 'refused', reason: refusalText('no_tonnage') };
  }
  const words = tonnageWords(t, null);
  if (!words) return { kind: 'refused', reason: refusalText('no_tonnage') };
  return {
    kind: 'value',
    perTon: Math.round(Math.round(record.cfm) / t.value),
    tonnage: words,
    bound: perTonBoundWords(rangeWords(record.range_status)?.bound, words.upperBound),
  };
};

/**
 * FS-878 — under a previous filter period's value on the Analysis page: its
 * CFM per ton using today's tonnage; why there is none (no usable tonnage,
 * worded as the current view words it); or, on a system of several Clips,
 * one line that it is not built. Nothing when the record has no CFM.
 */
export const PeriodCfmPerTon: React.FC<{ record: PeriodAirflowRecord; data: SystemAirflowResponse }> = ({
  record,
  data,
}) => {
  const result = pastPeriodPerTon(record, data);
  if (!result) return null;
  if (result.kind === 'several') {
    return (
      <p
        className="mt-2 text-xs text-gray-600"
        data-testid="period-cfm-per-ton-several"
        title={
          'Shown only for a system with one Clip that covers every return. On this system a past ' +
          "period's CFM per ton would need each return's airflow over the same dates, added up; " +
          "that is not built yet, so only this Clip's airflow is shown."
        }
      >
        {PAST_PER_TON_NEEDS_EVERY_RETURN}
      </p>
    );
  }
  if (result.kind === 'refused') {
    return (
      <div className="mt-2" data-testid="period-cfm-per-ton">
        <p className="text-sm">
          <span className="text-2xl font-semibold text-muted-foreground" aria-hidden="true">
            {DASH}
          </span>{' '}
          <span className="text-gray-600">CFM per ton</span>
        </p>{' '}
        <p
          className="text-xs text-amber-700"
          data-testid="period-cfm-per-ton-refusal"
          title="CFM per ton for a past period uses the system tonnage on file today, and only a usable one."
        >
          {result.reason}
        </p>
      </div>
    );
  }
  return (
    <div className="mt-2" data-testid="period-cfm-per-ton">
      <p className="text-sm">
        <span
          data-testid="period-cfm-per-ton-value"
          title={
            "This period's airflow divided by the system tonnage on file today, rounded." + result.bound.note
          }
        >
          <span className="text-2xl font-semibold">
            <BoundWord bound={result.bound.word} />
            {result.perTon.toLocaleString()}
          </span>{' '}
          <span className="text-gray-600">CFM per ton</span>
        </span>
        <NoteBadge
          label={TODAYS_TONNAGE}
          testId="period-cfm-per-ton-today"
          title={
            "This period's airflow, from its own filter and pressures, divided by the system tonnage " +
            'on file today. It is shown because this system has one Clip today. Equipment and Clip ' +
            'history are not tracked, so the tonnage or the number of Clips may have been different then.'
          }
        />
      </p>{' '}
      <p className="text-xs text-muted-foreground" title={result.tonnage.title}>
        today&apos;s tonnage: {result.tonnage.text}
      </p>
    </div>
  );
};

const SystemAirflowSection: React.FC<{
  data: SystemAirflowResponse;
  deviceId: number;
  /** FS-566: the Analysis page labels it as the current period. */
  heading?: string;
  headingTitle?: string;
}> = ({ data, deviceId, heading = 'System airflow', headingTitle }) =>
  isSystemAirflowV2(data) ? (
    <SystemAirflowV2
      data={data}
      deviceId={deviceId}
      heading={heading}
      headingTitle={headingTitle}
    />
  ) : (
    <LegacySystemAirflow data={data} deviceId={deviceId} />
  );

export default SystemAirflowSection;
